// First-run seeding. Populates the catalog + routines (always needed) and the
// section-10 baseline sessions (so "beat this" works day one). Idempotent and
// guarded by a meta flag; safe to call on every boot.

import { db } from '../db/db';
import { getMeta, setMeta } from '../db/repo';
import { EXERCISES } from './exercises';
import { ROUTINES } from './routines';
import { buildBaselineSessions } from './baselines';

// 8: per-gym increment overrides on the dumbbell exercises (Fitness First's rack
// steps 2 kg/hand, FitX 2.5), so the +/- buttons reach real weights at both gyms.
// 9: dips -> ramp_top_backoff (heavy top set + back-offs); assisted pull-ups
// replace straight-arm pulldown in Upper B.
const SEED_VERSION = '9';
const SEED_KEY = 'seedVersion';
const BASELINE_KEY = 'baselineSeeded';

export interface SeedResult {
  seededCatalog: boolean;
  seededBaselines: boolean;
}

export async function seedIfNeeded(today = new Date()): Promise<SeedResult> {
  const result: SeedResult = { seededCatalog: false, seededBaselines: false };

  const seeded = await getMeta(SEED_KEY);
  const exerciseCount = await db.exercises.count();

  if (seeded !== SEED_VERSION || exerciseCount === 0) {
    // bulkPut is an upsert: refreshes catalog/routine definitions without
    // clobbering any user-added exercises (different ids).
    await db.exercises.bulkPut(EXERCISES);
    await db.routines.bulkPut(ROUTINES);
    await setMeta(SEED_KEY, SEED_VERSION);
    result.seededCatalog = true;
  }

  // Baselines only once, and only into an empty session log, so we never overwrite
  // real logged sessions.
  const baselineSeeded = await getMeta(BASELINE_KEY);
  const sessionCount = await db.sessions.count();
  if (baselineSeeded !== '1' && sessionCount === 0) {
    const baselines = buildBaselineSessions(today);
    await db.sessions.bulkPut(baselines);
    await setMeta(BASELINE_KEY, '1');
    result.seededBaselines = true;
  }

  return result;
}
